/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import { Select } from 'foundations-components/transitional/components/Select';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Link } from 'foundations-components/transitional/components/Link';
import { Tabs, Tab } from 'foundations-components/transitional/components/Tabs';
import { View } from 'foundations-components/components/View';
import { List } from 'immutable';
import { Fragment, useEffect, useState } from 'react';
import { CRM_OBJECT, UNIFIED_EVENTS } from 'reporting-data/constants/dataTypes';
import { COLUMN_ROLES } from 'reporting-data/constants/relationalReports';
import { HUBSPOT_CRM_OBJECTS_BY_ID, getCrmObjectPluralName, isSupportedCrmObject } from 'reporting-data/crmObjects/utils';
import { isCustomObjectType } from 'reporting-data/lib/customObjects';
import { getUnifiedEventSchema } from 'reporting-data/lib/unifiedEvents/fetchSchema';
import { getSiblingDrilldownOptions } from 'reporting-data/lib/unifiedEvents/utils';
import getProperties from 'reporting-data/properties';
import { useSnowflakeJoinMetadata } from 'reporting-snowflake/relational/metadata/joins-with-meta';
import { resolveReportMeta } from 'reporting-snowflake/relational/metadata/report-meta';
import { getColumnAlias, getColumnField, getColumnLabel, getColumnRole } from 'reporting-snowflake/relational/utils/column-utils';
import { getTableLabel } from 'reporting-snowflake/relational/utils/table-utils';
import { CANCELLED_GROUPING, REMOVED_GROUPING, SELECTED_GROUPING, STARTED_GROUPING } from './DrilldownEvents';
import { isDurationDateProperty } from 'reporting-data/properties/propertyUtils';
import { getUsageTracker } from '../../monitoring/usageTracker';
import { EVENTS } from '../../lib/events';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const isValidDimension = property => {
  const validTypes = ['enumeration', 'string', 'buckets', 'bool', 'date', 'datetime'];
  const isDateDuration = isDurationDateProperty(property);
  return !property.get('hidden') && validTypes.includes(property.get('type')) && !property.get('blocklistedForGrouping') && !isDateDuration;
};
export function DrilldownDimensionControl({
  dimension,
  dimensionOptions,
  objectText,
  recordCount,
  canGroupData,
  canUngroupData,
  onChange,
  drilldownEventCallback,
  report
}) {
  const [isAddingDimension, setIsAddingDimension] = useState(false);
  const usageTracker = getUsageTracker();
  useEffect(() => {
    if (dimension === null) setIsAddingDimension(false);
  }, [dimension]);
  const isUnifiedEventsReport = report && report.getIn(['config', 'dataType']) === UNIFIED_EVENTS;
  const startAddingDimension = () => {
    drilldownEventCallback(STARTED_GROUPING);
    usageTracker.track(EVENTS.groupDrilldownData, {
      action: 'start-group-data'
    });
    setIsAddingDimension(true);
  };
  const onChangeDimension = ({
    target: {
      value
    }
  }) => {
    drilldownEventCallback(SELECTED_GROUPING, {
      property: value
    });
    usageTracker.track(EVENTS.groupDrilldownData, {
      action: isUnifiedEventsReport ? 'select-unified-events-tab' : 'select-group-data-dimension',
      property: value
    });
    onChange(value);
  };
  const clearDimension = () => {
    if (dimension) {
      drilldownEventCallback(REMOVED_GROUPING);
    } else {
      drilldownEventCallback(CANCELLED_GROUPING);
    }
    onChange(null);
    setIsAddingDimension(false);
  };
  if (isUnifiedEventsReport) {
    return /*#__PURE__*/_jsx(Tabs, {
      selected: dimension,
      onSelectedChange: onChangeDimension,
      use: "enclosed",
      marginBlock: "200",
      marginInlineEnd: "200",
      children: dimensionOptions && dimensionOptions.map(dimensionOption => {
        return /*#__PURE__*/_jsx(Tab, {
          tabId: dimensionOption.value,
          title: dimensionOption.text
        }, dimensionOption.value);
      })
    });
  }
  return /*#__PURE__*/_jsx(View, {
    marginBlock: "200",
    as: "div",
    children: dimension || isAddingDimension ? /*#__PURE__*/_jsxs(TFlex, {
      align: "stretch",
      children: [recordCount ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-ui-components.drilldownViewer.groupedBy",
        options: {
          objectName: objectText,
          recordCount: I18n.formatNumber(recordCount)
        }
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-ui-components.drilldownViewer.groupBy"
      }), /*#__PURE__*/_jsx(Select, {
        buttonUse: "link",
        clearable: canUngroupData,
        menuWidth: 300,
        onChange: onChangeDimension,
        options: dimensionOptions,
        placeholder: I18n.text('reporting-ui-components.drilldownViewer.selectADimension'),
        value: dimension,
        marginInline: "300"
      }), canUngroupData && /*#__PURE__*/_jsx(Link, {
        onClick: clearDimension,
        children: dimension ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-ui-components.drilldownViewer.ungroupData"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-ui-components.drilldownViewer.cancelGrouping"
        })
      })]
    }) : /*#__PURE__*/_jsxs(Fragment, {
      children: [recordCount && /*#__PURE__*/_jsxs(View, {
        marginInlineEnd: "300",
        as: "span",
        children: [/*#__PURE__*/_jsx(FormattedNumber, {
          value: recordCount
        }), " ", objectText]
      }), canGroupData && /*#__PURE__*/_jsx(Link, {
        onClick: startAddingDimension,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-ui-components.drilldownViewer.groupData"
        })
      })]
    })
  });
}
export function SnowflakeDimensionHandler({
  reportDefinition,
  dimension,
  setDimension,
  drilldownEventCallback,
  recordCount
}) {
  const [dimensionOptions, setDimensionOptions] = useState();
  const {
    data: allJoinMetadata
  } = useSnowflakeJoinMetadata();
  useEffect(() => {
    let expired = false;
    const dimensionColumns = reportDefinition.columns.filter(column => getColumnRole(column) === COLUMN_ROLES.DIMENSION);
    resolveReportMeta(reportDefinition).then(meta => {
      if (expired) {
        return;
      }
      const getSnowflakePropertyLabel = column => {
        const {
          table,
          name
        } = getColumnField(column);
        const snowflakeTable = meta.tables.get(table);
        const snowflakeProperty = meta.properties.getIn([table, name]);
        const maybeDatasetField = meta.datasetDefinition && meta.datasetDefinition.fields.get(name);
        return getColumnLabel(column, snowflakeProperty, snowflakeTable, maybeDatasetField, reportDefinition.expressions, reportDefinition.table, allJoinMetadata);
      };
      const getFieldTableLabel = field => {
        const table = field.table;
        const source = meta.tables.get(table);
        return getTableLabel(table, source);
      };
      const newDimensionOptions = dimensionColumns.map(column => [column, getColumnField(column)]).map(([column, field]) => [getSnowflakePropertyLabel(column), getFieldTableLabel(field), column]).map(([columnLabel, tableLabel, column]) => ({
        text: columnLabel,
        help: tableLabel,
        value: getColumnAlias(column)
      })).toArray();
      setDimensionOptions(newDimensionOptions);
    }).catch(e => {
      if (expired) {
        return;
      }
      // todo log errors to sentry or newrelic
      console.error(e);
    });
    return () => {
      expired = true;
    };
  }, [reportDefinition, allJoinMetadata]);
  const objectText = I18n.text(`reporting-ui-components.drilldownViewer.rowLabel`, {
    count: recordCount
  });
  return /*#__PURE__*/_jsx(DrilldownDimensionControl, {
    dimension: dimension,
    dimensionOptions: dimensionOptions,
    onChange: setDimension,
    drilldownEventCallback: drilldownEventCallback,
    objectText: objectText,
    recordCount: recordCount,
    canGroupData: true
  });
}
export const getDataTypeLabel = (dataType, objectTypeId) => {
  if (dataType === CRM_OBJECT) {
    if (isCustomObjectType(objectTypeId)) {
      return getCrmObjectPluralName(objectTypeId);
    }
    return Promise.resolve(I18n.text(`reporting-data.dataTypes.crmObjects.${HUBSPOT_CRM_OBJECTS_BY_ID.get(objectTypeId)}.pluralForm`));
  }
  return Promise.resolve(I18n.text(`reporting-data.dataTypes.${dataType}`));
};
export const getPropertyOptions = ({
  dataType,
  report,
  objectTypeId
}) => {
  if (dataType === UNIFIED_EVENTS) {
    return getUnifiedEventSchema(report.getIn(['config', 'unifiedEvent', 'reportType']), report.getIn(['config', 'unifiedEvent', 'schemaVersion'])).then(schema => getSiblingDrilldownOptions(schema, report));
  }
  const propertiesToHide = report.getIn(['displayParams', 'hidePropertiesOnDrilldown'], List());
  const dataTypeForProperties = isSupportedCrmObject(dataType) ? objectTypeId : dataType;
  return getProperties(dataTypeForProperties).then(properties => properties.get(dataTypeForProperties)).then(properties => properties.filter(isValidDimension)).then(properties => properties.filter(property => !propertiesToHide.includes(property.get('name')))).then(properties => properties.map((v, k) => ({
    text: v.get('label'),
    value: k
  })).valueSeq().toArray());
};
export default function DrilldownDimensionHandler({
  dataType,
  objectTypeId,
  dimension,
  drilldownEventCallback = () => {},
  recordCount,
  setDimension,
  canGroupData,
  report
}) {
  const [propertyOptions, setPropertyOptions] = useState(null);
  const [objectText, setObjectText] = useState('');
  useEffect(() => {
    getDataTypeLabel(dataType, objectTypeId).then(label => setObjectText(label));
  }, [dataType, objectTypeId]);
  useEffect(() => {
    getPropertyOptions({
      dataType,
      report,
      objectTypeId
    }).then(setPropertyOptions);
  }, [dataType, objectTypeId, report]);
  return /*#__PURE__*/_jsx(DrilldownDimensionControl, {
    dimension: dimension,
    dimensionOptions: propertyOptions,
    onChange: setDimension,
    drilldownEventCallback: drilldownEventCallback,
    objectText: objectText,
    recordCount: recordCount,
    canGroupData: canGroupData,
    canUngroupData: report.getIn(['config', 'dataType']) !== UNIFIED_EVENTS,
    report: report
  });
}
export const __TESTABLE__ = {
  isValidDimension
};